import {reservationTicketEmail} from '../../utils/reservation-ticket';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';

const MEETING_BASE_URL = 'https://meeting.sagel.app';

@Component({
    selector: 'app-manual-appointment',
    templateUrl: './manual-appointment.component.html',
    styleUrls: ['./manual-appointment.component.scss'],
    standalone: false
})
export class ManualAppointmentComponent implements OnInit {
  private ticketLink:string|null=null;
  companyName = '';
  private userId = '';
  today = new Date();

  // ── Stepper ───────────────────────────────────────────────────────────────
  currentStep = 1;
  readonly totalSteps = 2;
  stepValidationMessage = '';

  // ── Form ──────────────────────────────────────────────────────────────────
  startDate: Date | string = '';
  startTime = '09:00';
  name = '';
  email = '';
  phone = '';
  notes = '';

  // ── Meeting link ──────────────────────────────────────────────────────────
  addMeetingLink = true;
  meetingLinkMode: 'generate' | 'manual' = 'generate';
  manualMeetingLink = '';
  private readonly timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  private lang = 'en';

  // ── Status ────────────────────────────────────────────────────────────────
  isLoading = false;
  isSubmitting = false;
  showSavedNotification = false;
  showErrorNotification = false;
  errorText = '';
  reservationId = '';

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private translate: TranslateService,
  ) {
    const supported = ['en', 'tr', 'es', 'el', 'de'];
    this.translate.addLangs(supported);
    this.translate.setDefaultLang('en');
    const browserLang = navigator.language.split('-')[0];
    this.lang = supported.includes(browserLang) ? browserLang : 'en';
    this.translate.use(this.lang);
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const company = params.get('companyName');
      if (company) {
        this.companyName = decodeURIComponent(company);
        this.fetchUserId();
      } else {
        this.showErrorNotification = true;
        this.errorText = this.translate.instant('MANUAL_APPOINTMENT.ERROR_TEXT');
      }
    });
  }

  private fetchUserId(): void {
    this.isLoading = true;
    this.http
      .get<any>(`${environment.baseUrl}/users/get-id-by-company-name`, {
        params: { company_name: this.companyName },
      })
      .subscribe({
        next: (res) => {
          this.userId = res.user_id;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.showErrorNotification = true;
          this.errorText = this.translate.instant('MANUAL_APPOINTMENT.ERROR_TEXT');
        },
      });
  }

  // ── Meeting URL ───────────────────────────────────────────────────────────

  get timeOptions(): string[] {
    const times: string[] = [];
    for (let h = 6; h <= 23; h++) {
      for (const m of [0, 15, 30, 45]) {
        times.push(
          `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
        );
      }
    }
    return times;
  }

  get generatedMeetingUrl(): string {
    const dateKey = this.getDateKey(this.startDate);
    if (!dateKey || !this.startTime || !this.companyName) return '';
    const companySlug = this.slugify(this.companyName);
    const timePart = (this.startTime || '00:00').replace(':', '-');
    const timezonePart = this.timezone.replace(/\//g, '_').replace(/\s+/g, '');
    const code = `appointment-${dateKey}_${timePart}-${timezonePart}-${this.lang}`;
    return `${MEETING_BASE_URL}/${companySlug}/${encodeURIComponent(code)}`;
  }

  get effectiveMeetingUrl(): string {
    if (!this.addMeetingLink) return '';
    if (this.meetingLinkMode === 'manual') return this.manualMeetingLink.trim();
    return this.generatedMeetingUrl;
  }

  // ── Step validation ───────────────────────────────────────────────────────

  isStepOneValid(): boolean {
    return !!this.getDateKey(this.startDate) && !!this.startTime;
  }

  isStepTwoValid(): boolean {
    const hasName = !!String(this.name || '').trim();
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      String(this.email || '').trim(),
    );
    return hasName && validEmail;
  }

  canAccessStep(step: number): boolean {
    if (step <= 1) return true;
    if (step === 2) return this.isStepOneValid();
    return false;
  }

  goToStep(step: number): void {
    if (step < 1 || step > this.totalSteps) return;
    if (step === this.currentStep) return;
    if (step === 1) { this.currentStep = 1; return; }
    if (step === 2 && this.isStepOneValid()) {
      this.stepValidationMessage = '';
      this.currentStep = 2;
    }
  }

  nextStep(): void {
    this.stepValidationMessage = '';
    if (this.currentStep === 1 && !this.isStepOneValid()) {
      this.stepValidationMessage = this.translate.instant(
        'MANUAL_APPOINTMENT.STEP_ERROR_STEP1',
      );
      return;
    }
    if (this.currentStep < this.totalSteps) {
      this.currentStep += 1;
    }
  }

  prevStep(): void {
    this.stepValidationMessage = '';
    if (this.currentStep > 1) this.currentStep -= 1;
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (!this.isStepOneValid()) {
      this.currentStep = 1;
      this.stepValidationMessage = this.translate.instant(
        'MANUAL_APPOINTMENT.STEP_ERROR_STEP1',
      );
      return;
    }
    if (!this.isStepTwoValid()) {
      this.stepValidationMessage = this.translate.instant(
        'MANUAL_APPOINTMENT.STEP_ERROR_STEP2',
      );
      return;
    }

    this.stepValidationMessage = '';
    this.isSubmitting = true;

    this.http
      .get<{ reservationId: string }>(
        `${environment.baseUrl}/generate-reservation-id?userId=${this.userId}`,
      )
      .subscribe({
        next: (res) => {
          this.reservationId = res.reservationId;
          this.createAppointment();
        },
        error: () => this.onError('Failed to generate appointment ID.'),
      });
  }

  private createAppointment(): void {
    const dateKey = this.getDateKey(this.startDate) || '';
    const time =
      this.startTime.length === 5 ? `${this.startTime}:00` : this.startTime;
    const meetingUrl = this.effectiveMeetingUrl;

    const payload: any = {
      source: 'manual appointment',
      source_id: this.email,
      date: dateKey,
      time,
      name: this.name,
      email_address: this.email,
      customer_email: this.email,
      customer_phone: this.phone,
      service: this.notes,
      user_id: this.userId,
      id: this.reservationId,
      num_people: 1,
      pick_up: false,
      set_menu: 'default',
      salon_name: 'default',
    };

    if (meetingUrl) payload.meeting_url = meetingUrl;

    this.http
      .post(`${environment.baseUrl}/post/set_reservation`, payload)
      .subscribe({
        next: (res:any) => {
          this.ticketLink=res?.ticket_link||null;
          this.isSubmitting = false;
          this.showSavedNotification = true;
          this.sendConfirmationEmail(payload);
        },
        error: () => this.onError(),
      });
  }

  private onError(msg?: string): void {
    this.isSubmitting = false;
    this.showErrorNotification = true;
    this.errorText =
      msg || this.translate.instant('MANUAL_APPOINTMENT.ERROR_TEXT');
  }

  onCloseAlert(): void {
    this.showSavedNotification = false;
    this.showErrorNotification = false;
  }

  private sendConfirmationEmail(payload: any): void {
    if (!payload.email_address) return;
    const encodedCompany = encodeURIComponent(this.companyName);
    const editLink = `${environment.baseUrl}/#/edit-appointment/${encodedCompany}/${this.reservationId}`;
    const cancelLink = `${environment.baseUrl}/#/cancel/${encodedCompany}/${this.reservationId}`;
    const meetingUrl = payload.meeting_url || '';

    const dateObj = new Date(payload.date + 'T00:00:00');
    const longDate = dateObj.toLocaleDateString('en-GB', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    const displayTime = payload.time.slice(0, 5);

    const offsetMins = -new Date().getTimezoneOffset();
    const offsetH = Math.floor(Math.abs(offsetMins) / 60);
    const offsetM = Math.abs(offsetMins) % 60;
    const tzSign = offsetMins >= 0 ? '+' : '-';
    const tzDisplay = `${this.timezone} (UTC${tzSign}${String(offsetH).padStart(2, '0')}:${String(offsetM).padStart(2, '0')})`;

    const phoneRow = payload.customer_phone
      ? `<tr><td style="color:#5f6368;padding:5px 0;vertical-align:top;">Phone</td><td style="padding:5px 0;">${payload.customer_phone}</td></tr>`
      : '';
    const meetingDetailRow = meetingUrl
      ? `<tr><td style="color:#5f6368;padding:5px 0;vertical-align:top;">Meeting</td><td style="padding:5px 0;"><a href="${meetingUrl}" style="color:#1a73e8;word-break:break-all;">${meetingUrl}</a></td></tr>`
      : '';

    const html = `
<div style="font-family:'Google Sans','Roboto','Inter',Arial,sans-serif;background:#f1f3f4;padding:24px 16px;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0"
    style="max-width:600px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.12),0 4px 16px rgba(0,0,0,0.08);">
    <tr>
      <td style="background:linear-gradient(135deg,#1a73e8,#0d5bcc);padding:24px 32px 20px;">
        <div style="font-size:11px;letter-spacing:0.35em;text-transform:uppercase;color:rgba(255,255,255,0.7);margin-bottom:8px;">Appointment Confirmed</div>
        <div style="font-size:22px;font-weight:600;color:#fff;">${this.companyName}</div>
        <div style="margin-top:6px;font-size:12px;color:rgba(255,255,255,0.55);">ID: ${this.reservationId}</div>
      </td>
    </tr>
    <tr>
      <td style="padding:28px 32px 0;">
        <table width="100%" cellspacing="0" cellpadding="0"
          style="border:1px solid #dadce0;border-radius:12px;overflow:hidden;">
          <tr>
            <td colspan="2" style="background:#e8f0fe;padding:14px 20px;">
              <span style="font-size:15px;font-weight:600;color:#1a73e8;">&#128197; ${longDate}</span>
            </td>
          </tr>
          <tr>
            <td colspan="2" style="padding:16px 20px 12px;border-top:1px solid #e8eaed;">
              <div style="font-size:30px;font-weight:700;color:#202124;letter-spacing:-0.02em;">${displayTime}</div>
              <div style="font-size:12px;color:#5f6368;margin-top:4px;">&#127758;&nbsp;${tzDisplay}</div>
            </td>
          </tr>
          <tr>
            <td colspan="2" style="border-top:1px solid #e8eaed;padding:14px 20px;">
              <table width="100%" cellspacing="0" cellpadding="0" style="font-size:13px;color:#3c4043;">
                <tr>
                  <td style="width:110px;color:#5f6368;padding:5px 0;vertical-align:top;">Name</td>
                  <td style="padding:5px 0;font-weight:500;">${payload.name}</td>
                </tr>
                <tr>
                  <td style="color:#5f6368;padding:5px 0;vertical-align:top;">Email</td>
                  <td style="padding:5px 0;">${payload.email_address}</td>
                </tr>
                ${phoneRow}
                ${meetingDetailRow}
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
    ${meetingUrl ? `
    <tr>
      <td style="padding:24px 32px 8px;text-align:center;">
        <a href="${meetingUrl}"
          style="display:inline-block;padding:12px 32px;border-radius:24px;background:#1a73e8;color:#fff;text-decoration:none;font-weight:600;font-size:15px;letter-spacing:0.01em;box-shadow:0 1px 3px rgba(26,115,232,0.4);">
          &#127909;&nbsp; Join Meeting
        </a>
      </td>
    </tr>` : ''}
    <tr>
      <td style="padding:${meetingUrl ? '12px' : '24px'} 32px 24px;text-align:center;">
        <a href="${editLink}"
          style="display:inline-block;margin:4px 6px;padding:9px 22px;border-radius:6px;border:1.5px solid #dadce0;color:#1a73e8;text-decoration:none;font-size:13px;font-weight:500;">
          &#9998;&nbsp; Update appointment
        </a>
        <a href="${cancelLink}"
          style="display:inline-block;margin:4px 6px;padding:9px 22px;border-radius:6px;border:1.5px solid #dadce0;color:#d93025;text-decoration:none;font-size:13px;font-weight:500;">
          &#10005;&nbsp; Cancel appointment
        </a>
      </td>
    </tr>
    <tr>
      <td style="background:#f8f9fa;border-top:1px solid #e8eaed;padding:14px 24px;text-align:center;font-size:11px;color:#80868b;letter-spacing:0.25em;text-transform:uppercase;">
        Powered by Sagel
      </td>
    </tr>
  </table>
</div>`;

    this.http
      .post(`${environment.baseUrl}/email_notifications/info`, {
        header: `Appointment Confirmed – ${this.companyName}`,
        content: reservationTicketEmail(html,this.ticketLink,this.translate.instant('RESERVATION_TICKET.VIEW_MANAGE')),
        to_addr: payload.email_address,
      })
      .subscribe();
  }

  private getDateKey(
    value: Date | string | null | undefined,
  ): string | null {
    if (!value) return null;
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    const raw = String(value).trim();
    if (!raw) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}[./]\d{2}[./]\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split(/[./]/);
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) return null;
    const y = parsed.getFullYear();
    const mo = String(parsed.getMonth() + 1).padStart(2, '0');
    const da = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${mo}-${da}`;
  }

  private slugify(str: string): string {
    return str
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  get formattedStartDate(): string {
    const key = this.getDateKey(this.startDate);
    if (!key) return '';
    const d = new Date(key + 'T00:00:00');
    return d.toLocaleDateString(undefined, {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    });
  }
}
